import React, { useState, useRef, useEffect, useMemo } from "react";

import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { ReactComponent as LoginIcon } from "../../assets/icons/login.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user.svg";
import { ReactComponent as IMLogoDark } from "../../assets/images/logos/bilder-logo-dark.svg";
import { ReactComponent as IMLogoLight } from "../../assets/images/logos/bilder-logo-light.svg";
import { ReactComponent as CMLogoDark } from "../../assets/images/logos/communication-logo-dark.svg";
import { ReactComponent as CMLogoLight } from "../../assets/images/logos/communication-logo-light.svg";
import { ReactComponent as LensLogoDark } from "../../assets/images/logos/lens-logo-dark.svg";
import { ReactComponent as LensLogoLight } from "../../assets/images/logos/lens-logo-light.svg";
import { ReactComponent as MMLogoDark } from "../../assets/images/logos/media-manager-logo-dark.svg";
import { ReactComponent as MMLogoLight } from "../../assets/images/logos/media-manager-logo-light.svg";
import { ReactComponent as MBLogoDark } from "../../assets/images/logos/mediebank-logo-dark.svg";
import { ReactComponent as MBLogoLight } from "../../assets/images/logos/mediebank-logo-light.svg";
import { ReactComponent as PCLogoDark } from "../../assets/images/logos/press-client-logo-dark.svg";
import { ReactComponent as PCLogoLight } from "../../assets/images/logos/press-client-logo-light.svg";
import style from "../../assets/scss/components/user-menu.module.scss";
import { settingsAccountRoute } from "../../helpers/routes";
import AuthService from "../../services/AuthService";
import { selectUserAuthenticated, selectUserProfile } from "../../store/user/selector";
import UserExternalLink from "../helpers/UserExternalLink";

const authService = new AuthService();

function UserMenu({ isDarkMode }) {
  const { t } = useTranslation();

  const authenticated = useSelector(selectUserAuthenticated);
  const user = useSelector(selectUserProfile);

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [userMenuIsOpen, setUserMenuIsOpen] = useState(false);

  useEffect(() => {
    // Close dropdown if clicked outside of element
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setUserMenuIsOpen(false);
      }
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const handleDropdownClick = event => {
    if (dropdownRef.current) {
      for (const child of dropdownRef.current.children) {
        if (child.nodeName === "A" && child.contains(event.target)) {
          setUserMenuIsOpen(false);
          return;
        }
      }
    }
  };

  const triggerClicked = () => {
    if (!authenticated) {
      return login();
    }

    setUserMenuIsOpen(!userMenuIsOpen);
  };

  const logout = () => {
    authService.logout();
  };

  const login = () => {
    authService.login();
  };

  const computedDisplayName = useMemo(() => {
    if (!user) {
      return null;
    }
    const { firstName, lastName, username } = user;
    if (!firstName && !lastName) {
      return username;
    }

    return `${firstName || ""} ${lastName || ""}`;
  }, [user]);

  return (
    <>
      <div className={style["user-menu"]} ref={wrapperRef}>
        <button className={style["user-menu__trigger"]} onClick={triggerClicked}>
          {authenticated ? (
            <UserIcon className={style["user-menu__trigger__user-icon"]} />
          ) : (
            <LoginIcon className={style["user-menu__trigger__login-icon"]} />
          )}

          <div className={style["user-menu__trigger__name"]}>
            {authenticated ? (
              <span data-hj-suppress>{computedDisplayName}</span>
            ) : (
              t("user:Sign in")
            )}
          </div>
        </button>

        {userMenuIsOpen && (
          <div
            className={style["user-menu__dropdown"]}
            ref={dropdownRef}
            onClick={handleDropdownClick}
          >
            {(user.ntbInfoUser ||
              user.mediaManagerUser ||
              user.mediaBankUser ||
              user.newsServiceUser) && (
              <>
                <div className={style["user-menu__dropdown__text"]}>
                  {t("user:Your applications")}
                </div>
                <UserExternalLink
                  displayLogo
                  darkLogo={<CMLogoDark />}
                  isDarkMode={isDarkMode}
                  label={"Info"}
                  lightLogo={<CMLogoLight />}
                  url={"https://kommunikasjon.ntb.no/"}
                  visible={user.ntbInfoUser}
                  width="190px"
                />
                <UserExternalLink
                  displayLogo
                  darkLogo={<MMLogoDark />}
                  isDarkMode={isDarkMode}
                  label={"Media Manager"}
                  lightLogo={<MMLogoLight />}
                  url={"https://client.sdl.no"}
                  visible={user.mediaManagerUser}
                  width="188px"
                />
                <UserExternalLink
                  displayLogo
                  darkLogo={<LensLogoDark />}
                  isDarkMode={isDarkMode}
                  label={"Mediebank"}
                  lightLogo={<LensLogoLight />}
                  url={"https://mediebank.ntb.no/"}
                  visible={user.mediaBankUser}
                  width="88px"
                />
                <UserExternalLink
                  displayLogo
                  darkLogo={<IMLogoDark />}
                  isDarkMode={isDarkMode}
                  label={"Bilder og video"}
                  lightLogo={<IMLogoLight />}
                  url={"https://bilder.ntb.no/"}
                  visible={user.imageportalUser}
                  width="182px"
                />
                <UserExternalLink
                  displayLogo
                  darkLogo={<PCLogoDark />}
                  isDarkMode={isDarkMode}
                  label={"Presseklienten"}
                  lightLogo={<PCLogoLight />}
                  url={"https://presse.ops.ntb.no/"}
                  visible={true}
                  width="182px"
                />
                <div className={style["user-menu__dropdown__divider"]} />
              </>
            )}
            <Link className={style["user-menu__dropdown__item"]} to={settingsAccountRoute}>
              {t("user:Settings")}
            </Link>
            {user.roles.includes("admin") && (
              <a
                className={style["user-menu__dropdown__item"]}
                href="https://bilder.ntb.no/useradmin.action"
              >
                {t("user:Admin")}
              </a>
            )}
            <UserExternalLink label={"Hjelp"} url={"https://hjelp.ntb.no/"} />
            {(user.roles.includes("superadmin") || user.roles.includes("admin")) && (
              <div className={style["user-menu__dropdown__divider"]} />
            )}
            <div className={style["user-menu__dropdown__item"]} onClick={logout}>
              {t("user:Sign out")}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

UserMenu.propTypes = {
  isDarkMode: PropTypes.bool
};

export default UserMenu;
